import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import classNames from 'classnames';
import { FaPlus, FaChevronDown } from 'react-icons/fa';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import Modal from '../layouts/Modal';
import useSelectedCommunity from '../../hooks/community/useSelectedCommunity';
import useTaskStatuses from '../../hooks/community/useTaskStatuses';
import { taskService } from '../../services/taskService';
import { TaskStatus } from '../../types/task';
import Button from '../misc/Button';
import ParticipantPicker from '../misc/ParticipantPicker';
import DateTimePicker from '../forms/DateTimePicker';

const isSystemStatus = (status: TaskStatus): boolean => Boolean(status.systemKey);

const getStatusLabel = (status: TaskStatus, t: (key: string) => string): string => {
  if (status.systemKey === 'OPEN') return t('page.dashboard.tasks.defaultOpenStatus');
  if (status.systemKey === 'CLOSED') return t('page.dashboard.tasks.defaultClosedStatus');
  return status.name;
};

const STATUS_COLORS = [
  '#3B82F6',
  '#6B7280',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#EC4899',
  '#14B8A6',
  '#F97316',
  '#06B6D4',
];

interface CreateTaskModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onSuccess?: () => void;
}

export default function CreateTaskModal({ open, setOpen, onSuccess }: CreateTaskModalProps) {
  const { t } = useTranslation();
  const community = useSelectedCommunity();
  const { statuses, createStatus, updateStatus, deleteStatus, reload } = useTaskStatuses(community?.id);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState<Date | undefined>(undefined);
  const [assignees, setAssignees] = useState<number[]>([]);
  const [selectedStatusId, setSelectedStatusId] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showManageStatuses, setShowManageStatuses] = useState(false);

  useEffect(() => {
    if (!open) {
      setName('');
      setDescription('');
      setDueDate(undefined);
      setAssignees([]);
      setSelectedStatusId(undefined);
      setErrors({});
      setLoading(false);
      setShowManageStatuses(false);
    }
  }, [open]);

  // Default to first status once loaded
  useEffect(() => {
    if (statuses.length > 0 && selectedStatusId === undefined) {
      setSelectedStatusId(statuses[0].statusId);
    }
  }, [statuses, selectedStatusId]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = t('page.createTask.errors.nameRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreate = async () => {
    if (!community?.id) {
      toast.error(t('page.createTask.errors.noCommunity'));
      return;
    }

    if (!validate()) return;

    setLoading(true);

    try {
      await taskService.createTask(
        community.id,
        name.trim(),
        dueDate,
        assignees,
        selectedStatusId,
        description.trim() || undefined,
      );
      toast.success(t('page.dashboard.tasks.createSuccess'));
      setOpen(false);
      onSuccess?.();
    } catch (err: any) {
      console.error('Failed to create task:', err);
      toast.error(err?.response?.data?.message || t('page.createTask.errors.createError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      setOpen={(v) => {
        if (!showManageStatuses) setOpen(v);
      }}
      title={<>{t('page.createTask.title')}</>}
      footer={
        <div className="mt-4 flex justify-end gap-3">
          <Button type="button" variant="secondary" onClick={() => setOpen(false)} className="px-6 py-2">
            {t('general.cancel')}
          </Button>
          <Button type="button" variant="primary" disabled={loading} onClick={handleCreate} className="px-6 py-2">
            {loading ? t('page.createTask.creating') : t('page.createTask.create')}
          </Button>
        </div>
      }>
      <div className="flex flex-col gap-6">
        <div>
          <label htmlFor="task-name" className="mb-1 block text-sm font-medium text-primary">
            {t('page.createTask.name')}
          </label>
          <textarea
            id="task-name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (errors.name) setErrors({ ...errors, name: '' });
            }}
            placeholder={t('page.createTask.namePlaceholder')}
            rows={2}
            className={classNames(
              'w-full resize-none rounded-lg border-none !bg-secondary-50 p-2',
              errors.name && 'ring-2 ring-error',
            )}
          />
          {errors.name && <p className="mt-1 text-sm text-error">{errors.name}</p>}
        </div>

        <div>
          <label htmlFor="task-desc" className="mb-1 block text-sm font-medium text-primary">
            {t('page.createTask.description')}
          </label>
          <textarea
            id="task-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('page.createTask.descriptionPlaceholder')}
            rows={3}
            className="w-full resize-y rounded-lg border-none !bg-secondary-50 p-2"
          />
        </div>

        <div>
          <label htmlFor="task-due" className="mb-1 block text-sm font-medium text-primary">
            {t('page.createTask.dueDate')}
          </label>
          <DateTimePicker
            value={dueDate}
            onChange={(date) => {
              setDueDate(date ?? undefined);
            }}
            dateOnly
            className={classNames('')}
            ariaLabel={t('page.createTask.dueDate')}
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-primary">{t('page.createTask.assignees')}</label>
          <ParticipantPicker communityId={community?.id} selected={assignees} setSelected={setAssignees} />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-primary">{t('page.dashboard.tasks.status')}</label>
          <StatusPicker
            statuses={statuses}
            selectedStatusId={selectedStatusId}
            onSelect={setSelectedStatusId}
            onManage={() => setShowManageStatuses(true)}
          />
        </div>

        <ManageStatusesModal
          open={showManageStatuses}
          setOpen={setShowManageStatuses}
          statuses={statuses}
          onCreate={createStatus}
          onUpdate={(id, data) => updateStatus(id, data)}
          onDelete={deleteStatus}
          onReload={reload}
        />
      </div>
    </Modal>
  );
}

function StatusPicker({
  statuses,
  selectedStatusId,
  onSelect,
  onManage,
}: {
  statuses: TaskStatus[];
  selectedStatusId?: number;
  onSelect: (id: number | undefined) => void;
  onManage: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const selected = statuses.find((s) => s.statusId === selectedStatusId);

  return (
    <div className="flex gap-2">
      <div className="relative flex-1">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          className="flex w-full items-center gap-2 rounded-lg bg-secondary-50 px-3 py-2 text-sm text-primary">
          {selected ? (
            <>
              <span
                className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full"
                style={{ backgroundColor: selected.color }}
              />
              <span className="flex-1 text-left">{getStatusLabel(selected, t)}</span>
            </>
          ) : (
            <span className="flex-1 text-left text-details-500">{t('page.dashboard.tasks.statusPlaceholder')}</span>
          )}
          <FaChevronDown className="h-3 w-3 flex-shrink-0 text-details-500" />
        </button>
        {open && (
          <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-lg border border-secondary-200 bg-white shadow-lg">
            {statuses.map((s) => (
              <button
                key={s.statusId}
                type="button"
                onMouseDown={() => {
                  onSelect(s.statusId);
                  setOpen(false);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-primary hover:bg-secondary-50">
                <span
                  className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full"
                  style={{ backgroundColor: s.color }}
                />
                {getStatusLabel(s, t)}
              </button>
            ))}
          </div>
        )}
      </div>
      <Button
        variant="primary"
        onClick={onManage}
        aria-label={t('page.dashboard.tasks.manageStatuses')}
        title={t('page.dashboard.tasks.manageStatuses')}>
        <FiEdit2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

export function ManageStatusesModal({
  open,
  setOpen,
  statuses,
  onCreate,
  onUpdate,
  onDelete,
  onReload,
}: {
  open: boolean;
  setOpen: (v: boolean) => void;
  statuses: TaskStatus[];
  onCreate: (name: string, color?: string) => Promise<TaskStatus>;
  onUpdate: (id: number, data: { name?: string; color?: string; order?: number }) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  onReload: () => void;
}) {
  const { t } = useTranslation();
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(STATUS_COLORS[0]);
  const [showNewSwatches, setShowNewSwatches] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setNewName('');
      setNewColor(STATUS_COLORS[0]);
      setEditingId(null);
      setEditingName('');
    }
  }, [open]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await onCreate(newName.trim(), newColor);
      setNewName('');
      setNewColor(STATUS_COLORS[0]);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('page.dashboard.tasks.statusNameTaken'));
    } finally {
      setSaving(false);
    }
  };

  const handleRename = async (id: number) => {
    const status = statuses.find((item) => item.statusId === id);
    if (status && isSystemStatus(status)) {
      toast.error(t('page.dashboard.tasks.defaultStatusLocked'));
      setEditingId(null);
      return;
    }
    if (!editingName.trim()) {
      setEditingId(null);
      return;
    }
    setSaving(true);
    try {
      await onUpdate(id, { name: editingName.trim() });
      setEditingId(null);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    const status = statuses.find((item) => item.statusId === id);
    if (status && isSystemStatus(status)) {
      toast.error(t('page.dashboard.tasks.defaultStatusLocked'));
      return;
    }
    setSaving(true);
    try {
      await onDelete(id);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('page.dashboard.tasks.statusHasTasks'));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = (v: boolean) => {
    if (!v) onReload();
    setOpen(v);
  };

  return (
    <Modal
      open={open}
      setOpen={handleClose}
      title={<>{t('page.dashboard.tasks.manageStatuses')}</>}
      maxWidth="max-w-md"
      zIndex="z-[60]"
      footer={
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => handleClose(false)}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white">
            {t('general.close')}
          </button>
        </div>
      }>
      {statuses.length === 0 ? (
        <p className="mb-4 text-sm text-gray-400">{t('page.dashboard.tasks.noStatusesYet')}</p>
      ) : (
        <ul className="mb-4 flex flex-col gap-2">
          {statuses.map((s) => (
            <li key={s.statusId} className="flex items-center gap-2 rounded-lg border border-secondary-200 px-3 py-2">
              <ColorSwatchButton
                color={s.color}
                disabled={isSystemStatus(s)}
                onChange={(color) => onUpdate(s.statusId, { color })}
              />
              {editingId === s.statusId ? (
                <input
                  autoFocus
                  className="flex-1 !bg-secondary-50 text-sm text-primary focus:outline-none"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleRename(s.statusId);
                    if (e.key === 'Escape') setEditingId(null);
                  }}
                  onBlur={() => handleRename(s.statusId)}
                />
              ) : (
                <span className="flex-1 text-sm text-primary">{getStatusLabel(s, t)}</span>
              )}
              <button
                type="button"
                aria-label={t('general.edit')}
                disabled={saving || isSystemStatus(s)}
                onClick={() => {
                  setEditingId(s.statusId);
                  setEditingName(s.name);
                }}
                className="rounded p-1 text-details-500 hover:bg-secondary-50 hover:text-primary disabled:opacity-40">
                <FiEdit2 className="h-4 w-4" />
              </button>
              <button
                type="button"
                aria-label={t('general.delete')}
                disabled={saving || isSystemStatus(s)}
                onClick={() => handleDelete(s.statusId)}
                className="rounded p-1 text-details-500 hover:bg-secondary-50 hover:text-error disabled:opacity-40">
                <FiTrash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Add new */}
      <div className="flex items-center gap-2 rounded-lg border border-secondary-200 px-3 py-2">
        <div
          className="relative"
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) setShowNewSwatches(false);
          }}>
          <button
            type="button"
            aria-label={t('page.dashboard.tasks.pickColor')}
            className="h-5 w-5 flex-shrink-0 rounded-full border border-secondary-200"
            style={{ backgroundColor: newColor }}
            onClick={() => setShowNewSwatches((v) => !v)}
          />
          {showNewSwatches && (
            <div
              className="absolute bottom-7 left-0 z-50 flex flex-wrap gap-1 rounded-lg border border-secondary-200 bg-white p-2 shadow-lg"
              style={{ width: 116 }}>
              {STATUS_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-label={c}
                  className={classNames(
                    'h-5 w-5 rounded-full border-2',
                    c === newColor ? 'border-primary' : 'border-transparent hover:border-secondary-200',
                  )}
                  style={{ backgroundColor: c }}
                  onClick={() => {
                    setNewColor(c);
                    setShowNewSwatches(false);
                  }}
                />
              ))}
            </div>
          )}
        </div>
        <input
          type="text"
          className="flex-1 !bg-secondary-50 text-sm text-primary placeholder-details-500 focus:outline-none"
          placeholder={t('page.dashboard.tasks.newStatusPlaceholder')}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleCreate();
            }
          }}
        />
        <button
          type="button"
          disabled={saving || !newName.trim()}
          onClick={handleCreate}
          className="flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-40">
          <FaPlus className="h-3 w-3" />
          {t('page.dashboard.tasks.addStatus')}
        </button>
      </div>
    </Modal>
  );
}

function ColorSwatchButton({
  color,
  onChange,
  disabled = false,
}: {
  color: string;
  onChange: (c: string) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <div
      className="relative"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false);
      }}>
      <button
        type="button"
        aria-label={t('page.dashboard.tasks.pickColor')}
        className="h-3 w-3 flex-shrink-0 rounded-full border border-secondary-200 disabled:opacity-40"
        style={{ backgroundColor: color }}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
      />
      {open && (
        <div
          className="absolute left-0 top-5 z-50 flex flex-wrap gap-1 rounded-lg border border-secondary-200 bg-white p-2 shadow-lg"
          style={{ width: 116 }}>
          {STATUS_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              aria-label={c}
              className={classNames(
                'h-5 w-5 rounded-full border-2',
                c === color ? 'border-primary' : 'border-transparent hover:border-secondary-200',
              )}
              style={{ backgroundColor: c }}
              onClick={() => {
                onChange(c);
                setOpen(false);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
